import { HttpRequest } from '../../components/HttpRequest';

export const profileApi = {
  /**
   * Fetch customer profile details
   * API: GET /api/get_parentcustomer_data
   */
  async getCustomerProfile(customerId) {
    try {
      const response = await HttpRequest({
        method: 'GET',
        url: `/api/get_parentcustomer_data?customerId=${customerId}`
      });

      if (response?.status === 200 || response?.status === 201) {
        return {
          success: true,
          data: response?.response?.data,
          status: response?.status
        };
      }

      return {
        success: false,
        data: null,
        message: 'Failed to fetch customer profile',
        status: response?.status
      };
    } catch (error) {
      console.error('Error fetching customer profile:', error);
      return {
        success: false,
        data: null,
        error: error.message || 'Failed to fetch customer profile',
        status: 500
      };
    }
  },

  /**
   * Update customer profile details
   */
  async updateCustomerProfile(customerId, profileData) {
    try {
      const response = await HttpRequest({
        method: 'PUT',
        url: `/api/customer/${customerId}/profile`,
        body: profileData
      });

      if (response?.status === 200 || response?.status === 201) {
        return {
          success: true,
          data: response?.response?.data,
          message: 'Profile updated successfully',
          status: response?.status
        };
      }

      return {
        success: false,
        message: response?.response?.message || 'Failed to update profile',
        status: response?.status
      };
    } catch (error) {
      console.error('Error updating customer profile:', error);
      return {
        success: false,
        error: error.message || 'Failed to update profile',
        status: 500
      };
    }
  },

  /**
   * Change customer password
   * API: PUT /api/resetPinVersion2
   * Payload: {
   *   userPin: currentPassword,
   *   userNewPin: newPassword,
   *   type: 'ecommerce',
   *   userEmail: email
   * }
   */
  async changePassword(currentPassword, newPassword, email) {
    try {
      const payload = {
        userPin: currentPassword,
        userNewPin: newPassword,
        type: 'ecommerce',
        userEmail: email
      };

      const response = await HttpRequest({
        method: 'PUT',
        url: '/api/resetPinVersion2',
        body: payload
      });

      if (response?.response?.Status === 'SUCCESS') {
        return {
          success: true,
          message: 'Password changed successfully',
          status: 200
        };
      }

      return {
        success: false,
        message: response?.response?.message || 'Failed to change password',
        status: response?.status || 400
      };
    } catch (error) {
      console.error('Error changing password:', error);
      return {
        success: false,
        error: error.message || 'Failed to change password',
        status: 500
      };
    }
  },

  /**
   * Add new delivery address
   */
  async addAddress(customerId, addressData) {
    try {
      const payload = {
        customerId,
        ...addressData
      };

      const response = await HttpRequest({
        method: 'POST',
        url: '/api/customer/address',
        body: payload
      });

      if (response?.status === 200 || response?.status === 201) {
        return {
          success: true,
          data: response?.response?.data,
          message: 'Address added successfully',
          status: response?.status
        };
      }

      return {
        success: false,
        message: response?.response?.message || 'Failed to add address',
        status: response?.status
      };
    } catch (error) {
      console.error('Error adding address:', error);
      return {
        success: false,
        error: error.message || 'Failed to add address',
        status: 500
      };
    }
  },

  /**
   * Update existing address
   */
  async updateAddress(addressId, addressData) {
    try {
      const response = await HttpRequest({
        method: 'PUT',
        url: `/api/customer/address/${addressId}`,
        body: addressData
      });

      if (response?.status === 200 || response?.status === 201) {
        return {
          success: true,
          data: response?.response?.data,
          message: 'Address updated successfully',
          status: response?.status
        };
      }

      return {
        success: false,
        message: response?.response?.message || 'Failed to update address',
        status: response?.status
      };
    } catch (error) {
      console.error('Error updating address:', error);
      return {
        success: false,
        error: error.message || 'Failed to update address',
        status: 500
      };
    }
  },

  /**
   * Delete address
   */
  async deleteAddress(addressId) {
    try {
      const response = await HttpRequest({
        method: 'DELETE',
        url: `/api/customer/address/${addressId}`
      });

      if (response?.status === 200 || response?.status === 201) {
        return {
          success: true,
          message: 'Address deleted successfully',
          status: response?.status
        };
      }

      return {
        success: false,
        message: response?.response?.message || 'Failed to delete address',
        status: response?.status
      };
    } catch (error) {
      console.error('Error deleting address:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete address',
        status: 500
      };
    }
  },

  /**
   * Get customer wishlisted products
   * API: GET /api/v1/ecommerce/customer/{customerId}/wishlist
   * Supports pagination: ?page=1&limit=10&sort=createdAt&search=productName
   */
  async getWishlist(customerId, options = {}) {
    try {
      const { page = 1, limit = 10, sort = '-createdAt', search = '' } = options;

      let url = `/api/v1/ecommerce/customer/${customerId}/wishlist?page=${page}&limit=${limit}&sort=${sort}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await HttpRequest({
        method: 'GET',
        url
      });

      if (response?.status === 200 || response?.status === 201) {
        return {
          success: true,
          data: response?.response?.data || [],
          total: response?.response?.total || 0,
          page: response?.response?.page || page,
          limit: response?.response?.limit || limit,
          totalPages: response?.response?.totalPages || 0,
          status: response?.status
        };
      }

      return {
        success: false,
        data: [],
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
        message: 'Failed to fetch wishlist',
        status: response?.status
      };
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      return {
        success: false,
        data: [],
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
        error: error.message || 'Failed to fetch wishlist',
        status: 500
      };
    }
  },

  /**
   * Add product to wishlist
   * API: POST /api/v1/ecommerce/customer/{customerId}/wishlist
   * Body: { productId: "..." }
   * Returns: 201 on success, 400 for duplicate, 404 for not found
   */
  async addToWishlist(customerId, productId) {
    try {
      const response = await HttpRequest({
        method: 'POST',
        url: `/api/v1/ecommerce/customer/${customerId}/wishlist`,
        body: { productId }
      });

      if (response?.status === 201) {
        return {
          success: true,
          data: response?.response?.data,
          message: response?.response?.message || 'Product added to wishlist successfully',
          status: response?.status
        };
      }

      // Handle specific error cases
      if (response?.status === 400) {
        // Duplicate product or validation error
        return {
          success: false,
          message: response?.response?.message || 'Product already in wishlist',
          error: response?.response?.error || 'This product is already saved in your wishlist',
          errors: response?.response?.errors,
          status: response?.status
        };
      }

      if (response?.status === 404) {
        // Product or customer not found
        return {
          success: false,
          message: response?.response?.message || 'Product not found',
          error: response?.response?.error || 'The product you are trying to add does not exist',
          status: response?.status
        };
      }

      return {
        success: false,
        message: response?.response?.message || 'Failed to add to wishlist',
        status: response?.status
      };
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      return {
        success: false,
        error: error.message || 'Failed to add to wishlist',
        status: 500
      };
    }
  },

  /**
   * Remove product from wishlist
   * API: DELETE /api/v1/ecommerce/customer/{customerId}/wishlist/{productId}
   * Returns: 200 on success, 404 if product not in wishlist
   */
  async removeFromWishlist(customerId, productId) {
    try {
      const response = await HttpRequest({
        method: 'DELETE',
        url: `/api/v1/ecommerce/customer/${customerId}/wishlist/${productId}`
      });

      if (response?.status === 200) {
        return {
          success: true,
          data: response?.response?.data,
          message: response?.response?.message || 'Product removed from wishlist successfully',
          status: response?.status
        };
      }

      if (response?.status === 404) {
        return {
          success: false,
          message: response?.response?.message || 'Product not found in wishlist',
          error: response?.response?.error || 'This product is not in your wishlist',
          status: response?.status
        };
      }

      return {
        success: false,
        message: response?.response?.message || 'Failed to remove from wishlist',
        status: response?.status
      };
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return {
        success: false,
        error: error.message || 'Failed to remove from wishlist',
        status: 500
      };
    }
  },

  /**
   * Check if product is in wishlist
   * API: GET /api/v1/ecommerce/customer/{customerId}/wishlist/{productId}/exists
   * Optional endpoint for checking wishlist status
   */
  async checkWishlistItem(customerId, productId) {
    try {
      const response = await HttpRequest({
        method: 'GET',
        url: `/api/v1/ecommerce/customer/${customerId}/wishlist/${productId}/exists`
      });

      if (response?.status === 200) {
        return {
          success: true,
          data: response?.response?.data,
          inWishlist: response?.response?.data?.inWishlist || false,
          status: response?.status
        };
      }

      return {
        success: false,
        inWishlist: false,
        status: response?.status
      };
    } catch (error) {
      console.error('Error checking wishlist item:', error);
      return {
        success: false,
        inWishlist: false,
        error: error.message || 'Failed to check wishlist',
        status: 500
      };
    }
  },

  /**
   * Clear entire wishlist
   * API: DELETE /api/v1/ecommerce/customer/{customerId}/wishlist
   * Optional endpoint for clearing all wishlist items
   */
  async clearWishlist(customerId) {
    try {
      const response = await HttpRequest({
        method: 'DELETE',
        url: `/api/v1/ecommerce/customer/${customerId}/wishlist`
      });

      if (response?.status === 200) {
        return {
          success: true,
          data: response?.response?.data,
          message: response?.response?.message || 'Wishlist cleared successfully',
          status: response?.status
        };
      }

      return {
        success: false,
        message: response?.response?.message || 'Failed to clear wishlist',
        status: response?.status
      };
    } catch (error) {
      console.error('Error clearing wishlist:', error);
      return {
        success: false,
        error: error.message || 'Failed to clear wishlist',
        status: 500
      };
    }
  },

  /**
   * Update email notification preferences
   */
  async updateEmailPreferences(customerId, preferences) {
    try {
      const response = await HttpRequest({
        method: 'PUT',
        url: `/api/customer/${customerId}/email-preferences`,
        body: preferences
      });

      if (response?.status === 200 || response?.status === 201) {
        return {
          success: true,
          message: 'Email preferences updated',
          status: response?.status
        };
      }

      return {
        success: false,
        message: response?.response?.message || 'Failed to update preferences',
        status: response?.status
      };
    } catch (error) {
      console.error('Error updating email preferences:', error);
      return {
        success: false,
        error: error.message || 'Failed to update preferences',
        status: 500
      };
    }
  },

  /**
   * Get customer addresses from profile
   * Fetches from /api/get_parentcustomer_data and extracts address data
   */
  async getCustomerAddresses(customerId) {
    try {
      const response = await HttpRequest({
        method: 'GET',
        url: `/api/get_parentcustomer_data?customerId=${customerId}`
      });

      if (response?.status === 200 || response?.status === 201) {
        const customerData = response?.response?.data;
        // Extract address from both customerpreference and address fields
        const address = customerData?.customerpreference || customerData?.address || {};

        // Return in array format for compatibility with address list display
        const addresses = address && Object.keys(address).length > 0
          ? [{
              _id: customerId, // Use customerId as address ID
              address: address.address || '',
              city: address.city || '',
              county: address.county || '',
              postalCode: address.postalCode || '',
              apartment: address.apartment || '',
              isPrimary: true // Mark as primary address
            }]
          : [];

        return {
          success: true,
          data: addresses,
          status: response?.status
        };
      }

      return {
        success: false,
        data: [],
        message: 'Failed to fetch customer addresses',
        status: response?.status
      };
    } catch (error) {
      console.error('Error fetching customer addresses:', error);
      return {
        success: false,
        data: [],
        error: error.message || 'Failed to fetch customer addresses',
        status: 500
      };
    }
  },

  /**
   * Delete customer account
   */
  async deleteAccount(customerId, password) {
    try {
      const response = await HttpRequest({
        method: 'DELETE',
        url: `/api/customer/${customerId}/account`,
        body: { password }
      });

      if (response?.status === 200 || response?.status === 201) {
        return {
          success: true,
          message: 'Account deleted successfully',
          status: response?.status
        };
      }

      return {
        success: false,
        message: response?.response?.message || 'Failed to delete account',
        status: response?.status
      };
    } catch (error) {
      console.error('Error deleting account:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete account',
        status: 500
      };
    }
  }
};
